import { watch } from 'vue'
import { storeToRefs } from 'pinia'
import {
  deviceManagementAcceptorsService,
  DMAcceptorsError,
  useDeviceManagementAcceptorsStore,
  type HookErrorResponse
} from '@nsftx/seven-client-retail-vue-dm'
import i18n from '@/plugins/translationsPlugin'
import { logService } from '@nsftx/seven-client-retail-utils'
import { useNotificationsStore } from '@nsftx/seven-nxg-retail-vue-sdk'
import errorParser from '@/utils/errorParser'
import type { PrinterPaperStatusChangeAction } from '../print/printerTypes'

const LOG_PREFIX = '[acceptorsService]'

const whitelistedStartAcceptorsErrorMessages: string[] = [
  'acceptors-start-error-tbo',
  'acceptors-start-error-device-cash-deposit',
  'acceptors-start-error-oktopay',
  'acceptors-start-error-working-time',
  'acceptors-start-error-paper-low',
  'acceptors-start-error-paper-out',
  'acceptors-start-error-printer-not-selected'
]

const filterStartAcceptorsErrorMessages = (
  unfilteredErrorMessages: Array<HookErrorResponse>
): Array<HookErrorResponse> =>
  unfilteredErrorMessages.filter((errorMessage) =>
    whitelistedStartAcceptorsErrorMessages.includes(errorMessage.id || '')
  )

const isHookErrorResponse = (obj: unknown): obj is HookErrorResponse => {
  return typeof obj === 'object' && obj !== null && 'message' in obj && 'code' in obj
}

const isHookErrorResponseArray = (arr: unknown): arr is HookErrorResponse[] => {
  return Array.isArray(arr) && arr.every(isHookErrorResponse)
}

const showDMAcceptorsErrorNotification = (error: DMAcceptorsError | Array<HookErrorResponse>) => {
  const notificationsStore = useNotificationsStore()
  const { t } = i18n.global

  if (isHookErrorResponseArray(error)) {
    const whitelistedErrors = filterStartAcceptorsErrorMessages(error)

    whitelistedErrors.forEach((errorMessage) => {
      notificationsStore.showNotification({
        id: errorMessage.id,
        text: [t(errorMessage.code.toLowerCase())],
        variant: 'error',
        timeout: null
      })
    })
  } else {
    notificationsStore.showNotification({
      text: [t(error.code.toLowerCase())],
      variant: 'error',
      timeout: null
    })
  }
}

const initAcceptors = () => {
  deviceManagementAcceptorsService
    .init()
    .catch((error: DMAcceptorsError | Array<HookErrorResponse>) => {
      showDMAcceptorsErrorNotification(error)
    })

  deviceManagementAcceptorsService.detectInitialAcceptorsState()
  handleAcceptorsStartBlockersState()
}

const handleAcceptorsStartBlockersState = () => {
  const { acceptorStartBlockers } = storeToRefs(useDeviceManagementAcceptorsStore())

  watch(
    acceptorStartBlockers,
    () => {
      handleAcceptorStartBlockersNotification()
    },
    { deep: true }
  )
}

const closeDMAcceptorsErrorNotifications = () => {
  const notificationsStore = useNotificationsStore()

  whitelistedStartAcceptorsErrorMessages.forEach((messageId) => {
    notificationsStore.closeNotificationById(messageId)
  })
}

const handleAcceptorsStateOnPrinterStatusChange = (
  isStatusActive: boolean,
  cause: PrinterPaperStatusChangeAction,
  notificationId: string
) => {
  const notificationsStore = useNotificationsStore()

  if (isStatusActive) {
    handleAcceptorsStateChange(cause, 'STOP')
  } else {
    notificationsStore.closeNotificationById(notificationId)
    handleAcceptorsStateChange(cause, 'START')
  }
}

const handleAcceptorsStateChange = (
  cause: PrinterPaperStatusChangeAction,
  action: 'START' | 'STOP'
) => {
  const acceptorsStateChangeLogMessage = `${LOG_PREFIX} Calling acceptors ${action} action`
  const acceptorsStateChangeActionFn =
    action === 'START'
      ? deviceManagementAcceptorsService.startAcceptors
      : deviceManagementAcceptorsService.stopAcceptors
  const printerCause = `Printer${cause.charAt(0).toUpperCase()}${cause.slice(1)}`

  logService.info(acceptorsStateChangeLogMessage, {
    code: `ST_PRINTER_STATUS_CHANGE_ACCEPTORS_${action}`,
    details: {
      action,
      cause: printerCause
    }
  })

  if (action === 'START') {
    closeDMAcceptorsErrorNotifications()
  }

  acceptorsStateChangeActionFn().catch((error) => {
    logService.warn(`${LOG_PREFIX} Acceptors ${action} action failed.`, {
      ...errorParser.parseUpstream(error),
      code: `ST_ACCEPTORS_${action}_ERROR`,
      details: error
    })

    showDMAcceptorsErrorNotification(error)
  })
}

const handleAcceptorStartBlockersNotification = () => {
  const { acceptorStartBlockers } = storeToRefs(useDeviceManagementAcceptorsStore())
  const notificationsStore = useNotificationsStore()
  const { t } = i18n.global
  const devices: Array<keyof typeof acceptorStartBlockers.value> = ['bill', 'coin']

  devices.forEach((device) => {
    const id = `acceptors_start_blocker_${device}`
    const blockers = acceptorStartBlockers.value[device]

    notificationsStore.closeNotificationById(id)

    if (blockers.length) {
      const blockerMessages = blockers.map((blockerKey: string) =>
        t(`acceptors_start_blocker_${blockerKey}`)
      )

      notificationsStore.showNotification({
        id,
        text: [t(`${id}_title`), ...blockerMessages],
        variant: 'error',
        timeout: null
      })
    }
  })
}

export {
  initAcceptors,
  handleAcceptorsStateOnPrinterStatusChange,
  showDMAcceptorsErrorNotification,
  closeDMAcceptorsErrorNotifications
}
